import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import Table, {
  buttonsStyle,
  dateFormatter,
} from "../../../components/tables/table";
import { Button, TextField, MenuItem, Tooltip } from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import { alertError } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { getRunnerSymbolDecisions } from "../../../../api/runnerSymbolDecisions";

const PHASE_OPTIONS = [
  "",
  "prefilter_metrics",
  "prefilter_queue",
  "runner_no_order",
];

/** Alineado con enum `strategy` del modelo backend (listado / filtro). */
const STRATEGY_OPTIONS = [
  "",
  "Scalping",
  "Slope",
  "Hedge",
  "Rebuys",
  "unknown",
];

const emptyFilters = () => ({
  strategyId: "",
  strategy: "",
  phase: "",
  reasonCode: "",
  canonicalSymbol: "",
  exchange: "",
  from: "",
  to: "",
});

function buildRow(item) {
  const evals = item.reasonDetail?.watchSession?.evaluationsCount;
  const watchLabel =
    item.watchStatus === "active" && evals != null
      ? `activa (${evals})`
      : item.watchStatus || "";
  return {
    id: String(item._id),
    _id: item._id,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    strategy: item.strategy || "—",
    phase: item.phase || "—",
    reasonCode: item.reasonCode || "—",
    canonicalSymbol: item.canonicalSymbol || "—",
    exchange: item.exchange || "—",
    strategyId: item.strategyId || "—",
    watchStatus: watchLabel || "—",
  };
}

export default function RunnerSymbolDecisionsPage() {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState(() => emptyFilters());
  const [applied, setApplied] = useState(() => emptyFilters());

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit, skip };
    if (applied.strategyId.trim()) params.strategyId = applied.strategyId.trim();
    if (applied.strategy) params.strategy = applied.strategy;
    if (applied.phase) params.phase = applied.phase;
    if (applied.reasonCode.trim()) params.reasonCode = applied.reasonCode.trim();
    if (applied.canonicalSymbol.trim())
      params.canonicalSymbol = applied.canonicalSymbol.trim();
    if (applied.exchange.trim()) params.exchange = applied.exchange.trim();
    if (applied.from) params.from = new Date(applied.from).toISOString();
    if (applied.to) params.to = new Date(applied.to).toISOString();

    getRunnerSymbolDecisions(params)
      .then((res) => {
        if (res.status === 200 && res.data) {
          const rows = (res.data.items || []).map(buildRow);
          setItems(rows);
          setTotal(
            typeof res.data.total === "number" ? res.data.total : rows.length,
          );
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudieron cargar las decisiones de símbolo.",
        });
      })
      .finally(() => setLoading(false));
  }, [limit, skip, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setSkip(0);
  };

  const resetFilters = () => {
    const z = emptyFilters();
    setDraft(z);
    setApplied(z);
    setSkip(0);
  };

  function actionsFormatter(cell) {
    return (
      <Tooltip title="Ver detalle (JSON)">
        <Button
          style={buttonsStyle}
          size="small"
          onClick={() => history.push(`/runner-symbol-decisions/${cell}`)}
        >
          <Visibility />
        </Button>
      </Tooltip>
    );
  }

  const columns = [
    { dataField: "updatedAt", text: "Actualizado", formatter: dateFormatter },
    { dataField: "strategy", text: "Estrategia" },
    { dataField: "phase", text: "Fase" },
    { dataField: "reasonCode", text: "Motivo" },
    { dataField: "watchStatus", text: "Vigilancia" },
    { dataField: "canonicalSymbol", text: "Par" },
    { dataField: "exchange", text: "Exchange" },
    { dataField: "strategyId", text: "StrategyId" },
    {
      dataField: "id",
      text: "",
      formatter: actionsFormatter,
      headerStyle: { width: "80px" },
    },
  ];

  const page = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const setDraftField = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <Card>
      <CardHeader title="Decisiones símbolo (hub)" />
      <CardBody>
        <p className="text-muted mb-3">
          Auditoría del hub: prefiltro Scalping (métricas / cola), runner sin{" "}
          <code>createOrder</code> (Scalping / Slope), y códigos de error globales
          Slope (<code>_global_</code>). Desactivar en hub:{" "}
          <code>RUNNER_SYMBOL_DECISION_LOG=0</code>.
        </p>
        <div className="row mb-4">
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="strategyId (Mongo)"
              value={draft.strategyId}
              onChange={setDraftField("strategyId")}
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              select
              fullWidth
              label="Estrategia"
              value={draft.strategy}
              onChange={setDraftField("strategy")}
              variant="outlined"
              size="small"
            >
              {STRATEGY_OPTIONS.map((s) => (
                <MenuItem key={s || "all-strategy"} value={s}>
                  {s || "(todas)"}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              select
              fullWidth
              label="Fase"
              value={draft.phase}
              onChange={setDraftField("phase")}
              variant="outlined"
              size="small"
            >
              {PHASE_OPTIONS.map((s) => (
                <MenuItem key={s || "all"} value={s}>
                  {s || "(todas)"}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="reasonCode"
              value={draft.reasonCode}
              onChange={setDraftField("reasonCode")}
              placeholder="PARAMS_FAILED"
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Par canónico"
              value={draft.canonicalSymbol}
              onChange={setDraftField("canonicalSymbol")}
              placeholder="BTC-USDT"
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Exchange"
              value={draft.exchange}
              onChange={setDraftField("exchange")}
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Desde"
              type="datetime-local"
              value={draft.from}
              onChange={setDraftField("from")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Hasta"
              type="datetime-local"
              value={draft.to}
              onChange={setDraftField("to")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="col-md-3 mb-2 d-flex align-items-end">
            <Button
              color="primary"
              variant="contained"
              className="mr-2"
              onClick={applyFilters}
              disabled={loading}
            >
              Filtrar
            </Button>
            <Button variant="outlined" onClick={resetFilters} disabled={loading}>
              Limpiar
            </Button>
          </div>
        </div>

        {loading && <p>Cargando…</p>}
        {!loading && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">
                Total: {total} · Página {page} de {totalPages}
              </span>
              <div>
                <Button
                  size="small"
                  disabled={skip <= 0 || loading}
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                >
                  Anterior
                </Button>
                <Button
                  size="small"
                  className="ml-2"
                  disabled={skip + limit >= total || loading}
                  onClick={() => setSkip(skip + limit)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
            <Table data={items} columns={columns} search={false} paginate={false} />
          </>
        )}
      </CardBody>
    </Card>
  );
}
